/**
 * A policy title, guessed from the file's name.
 *
 * The file somebody uploads was named by somebody — often the same person,
 * often well: "Hand-Hygiene-and-Standard-Precautions.pdf". Making them type
 * that again into the Title box is the kind of small tax that makes a form
 * feel like work. So if the Title is still empty when a file arrives, this
 * takes its best guess from the name and leaves it editable.
 *
 * "AS WELL AS YOU CAN" is the brief, and the rules below are the reading of
 * it. Each is a thing real filenames do:
 *
 *   - the extension goes                        "….pdf"
 *   - separators become spaces                   "hand_hygiene", "hand-hygiene"
 *   - words jammed together come apart           "HandHygieneProcedure"
 *   - dates and version marks go                 "2024-03-15", "v2", "rev3"
 *   - working-copy words go                      "FINAL", "draft", "copy", "(2)"
 *   - shouting and mumbling are title-cased      "HAND HYGIENE", "hand hygiene"
 *   - a name already in mixed case is trusted    "Restraint Use in the ICU"
 *
 * What it does NOT do: strip a leading code like "IC-0114". That might be a
 * policy number the person wants in the title, and a guess that deletes
 * information is worse than one that leaves a little extra to trim.
 *
 * The result may be empty — "v2 (3).pdf" is nothing but noise — and a caller
 * must treat empty as "no guess" rather than as a title.
 */

import { TITLE_MAX } from '../../types/fieldLimits';

/**
 * Words that describe the file's status, not the policy's subject.
 *
 * Stripped only from the END of a name. "Hand Hygiene FINAL" is a status;
 * "Final Rule Compliance" is a subject, and a token-anywhere rule would gut
 * it. A leading "Draft" is kept for the same reason — a person can trim a
 * word more easily than they can notice one missing.
 */
const STATUS = /^(final|draft|copy|approved|signed)$/i;

/**
 * A token that is a code rather than a word: "COVID-19", "IC-0114", "H1N1",
 * "3.2". Short letters, then digits. Its hyphens and dots are part of it and
 * survive; a word's are separators and do not. "hygiene-2024" has too many
 * letters to be a code and splits into "Hygiene 2024", which is right.
 */
const CODE = /^(?:[A-Za-z]{1,6}-?)?\d+(?:[.-][A-Za-z0-9]+)*$/;

/** Words that stay lowercase in a title unless they lead it. */
const SMALL = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to']);

function titleCase(token: string, first: boolean): string {
  // "COVID-19", "H1N1", "5S": a code, not a word. Only reached when the whole
  // name is one case and is being re-cased anyway, so a lowercase code comes
  // up to how codes are written; a mixed-case name never gets here.
  if (/\d/.test(token)) return token.toUpperCase();
  // A lone capital — "Appendix B", "Test A" — is a label, not the article
  // "a". Keep it as written. A lone lowercase "a" is still the article.
  if (token.length === 1 && token === token.toUpperCase()) return token;
  const lower = token.toLowerCase();
  if (!first && SMALL.has(lower)) return lower;
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function titleFromFileName(fileName: string): string {
  let s = fileName.trim();

  // The extension, but only a plausible one: "policy.pdf" loses ".pdf",
  // "Section 3.2 Restraints" keeps its ".2".
  s = s.replace(/\.(pdf|docx?|txt|md|rtf|odt|pages)$/i, '');

  // Dates and version marks, BEFORE separators are touched — "2024-03-15" is
  // one thing to remove, not three numbers to keep, and "v2.1" is one mark,
  // not a version followed by a stray "1".
  //
  // Lookarounds, not \b: an underscore is a "word" character, so \b never
  // fires between "policy_" and "2024", and "hand_hygiene_v2" kept its v2.
  // Found by dropping a real file in the browser, not by the tests.
  s = s.replace(/(?<![A-Za-z0-9])\d{4}[-_.]?\d{2}[-_.]?\d{2}(?![A-Za-z0-9])/g, ' ');
  s = s.replace(/(?<![A-Za-z0-9])(?:v|rev)\d+(?:\.\d+)*(?![A-Za-z0-9])/gi, ' ');

  // Duplicate-download suffixes: "(1)", "(2)".
  s = s.replace(/\(\d+\)/g, ' ');

  // Words jammed together: "HandHygiene" → "Hand Hygiene". A lowercase letter
  // or digit followed by a capital is a word boundary somebody elided.
  s = s.replace(/([a-z0-9])([A-Z])/g, '$1 $2');

  // Separators — but a code keeps its own punctuation. See CODE.
  const tokens = s
    .split(/[\s_+]+/)
    .flatMap((t) => (CODE.test(t) ? [t] : t.split(/[-.]+/)))
    .filter(Boolean);

  // Trailing status words.
  while (tokens.length && STATUS.test(tokens[tokens.length - 1]!)) tokens.pop();
  if (tokens.length === 0) return '';

  // Judge the casing on the WORDS. A code is spelt however codes are spelt,
  // and "COVID-19_visitor_policy" is a lowercase name with a code in it — not
  // a mixed-case name to be trusted, which is what it read as when the code's
  // capitals were counted. A single letter is not a word for this purpose
  // either: "bulk-test-A-hand-hygiene" is a lowercase name with an "A" in it,
  // and it came out lowercase until this excluded it.
  const words = tokens.filter((t) => !CODE.test(t) && t.replace(/[^A-Za-z]/g, '').length > 1);
  // A name with no multi-letter word at all ("a.pdf") is judged on what it has.
  const letters = (words.length ? words : tokens).join('').replace(/[^A-Za-z]/g, '');
  const shouting = letters.length > 0 && letters === letters.toUpperCase();
  const mumbling = letters.length > 0 && letters === letters.toLowerCase();

  // Mixed case was chosen by a person and is kept. All one case was not.
  const cased = shouting || mumbling ? tokens.map((t, i) => titleCase(t, i === 0)) : tokens;

  return cased.join(' ').slice(0, TITLE_MAX).trim();
}
